import {
	ComponentRef,
	DestroyRef,
	Directive,
	ElementRef,
	Renderer2,
	TemplateRef,
	ViewContainerRef,
	computed,
	inject,
	input,
	signal
} from '@angular/core';
import { CommonColors, TooltipOptions } from '@inscomponents/core';
import { match, P } from 'ts-pattern';
import { SklTooltipComponent, TooltipData, TooltipPosition } from './tooltip.component';

export { TooltipData, TooltipPosition } from './tooltip.component';

export interface TooltipObjectProp extends Omit<TooltipOptions, 'position'> {
	text: string;
	delay?: number;
	color?: CommonColors;
	position?: TooltipPosition;
}

const INNER_TOOLTIP_SELECTOR =
	'[data-has-tooltip], [tooltip]:not([tooltip=""]), [data-tooltip]:not([data-tooltip=""]), [title]:not([title=""]), ins-tooltip, [ins-tooltip], [insTooltip]';
const DEFAULT_TOOLTIP_DELAY = 500;

@Directive({
	selector: '[tooltip]',
	host: {
		'(pointerenter)': 'showTooltip($event)',
		'(pointerleave)': 'hideTooltip($event)',
		'(mouseleave)': 'hideTooltip($event)',
		'(pointermove)': 'onPointerMove($event)',
		'(press)': 'showTooltip($event, true)',
		'(pressup)': 'hideTooltip()',
		'[style.anchor-name]': 'anchorName',
		'[attr.aria-describedby]': 'hasTooltip() ? tooltipId : null',
		'[attr.data-has-tooltip]': 'hasTooltip() ? "" : null',
		'[style.-webkit-touch-callout]': '"none"',
	}
})
export class TooltipDirective {
	readonly #viewContainerRef = inject(ViewContainerRef);
	readonly #destroyRef = inject(DestroyRef);
	readonly #renderer = inject(Renderer2);
	readonly #elmRef = inject(ElementRef<HTMLElement>);
	readonly #parentTooltip = inject(TooltipDirective, { optional: true, skipSelf: true });

	readonly tooltip = input<string | TemplateRef<any> | TooltipData | string[] | null>();
	readonly tooltipContainerRef = input<HTMLElement | null>();
	readonly tooltipDelay = input<number>(DEFAULT_TOOLTIP_DELAY, { alias: 'delay' });
	readonly tooltipPosition = input<TooltipPosition>('bottom', { alias: 'position' });

	readonly #programmaticTooltip = signal<string | TemplateRef<any> | TooltipData | string[] | null>(null);

	readonly #getTooltipContent = computed(() => this.#programmaticTooltip() ?? this.tooltip() ?? null);

	protected readonly hasTooltip = computed(() => {
		const content = this.#getTooltipContent();
		if (!content) return false;

		return match(content)
			.with(P.instanceOf(TemplateRef), () => true)
			.with(P.string, (str) => str.trim().length > 0)
			.with(P.array(P.string), (arr) => arr.length > 0)
			.with({ text: P.string }, (data) => data.text.trim().length > 0)
			.with({ text: P.array(P.string) }, (data) => data.text.length > 0)
			.otherwise(() => false);
	});

	#componentRef: ComponentRef<SklTooltipComponent> | null = null;
	readonly #id = crypto.randomUUID().replaceAll('-', '');
	readonly anchorName = `--tooltip-anchor-${this.#id}` as const;
	readonly tooltipId = `tooltip-${this.#id}` as const;

	#timeout: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		this.#destroyRef.onDestroy(() => {
			this.#clearTimeout();
			this.#removeTooltip();
		});
	}

	setTooltip(val: string | TemplateRef<any> | TooltipData | string[] | null) {
		this.#programmaticTooltip.set(val);
	}

	showTooltip(event?: Event, immediate = false) {
		if (event instanceof PointerEvent && event.pointerType !== 'mouse' && !immediate) {
			return;
		}
		if (!this.hasTooltip() || this.#hasInnerTooltip(event) || this.#isTooltipActive()) {
			if (this.#hasInnerTooltip(event) && this.#isTooltipActive()) {
				this.hideTooltip();
			}
			return;
		}

		this.#hideParentTooltips();

		if (immediate) {
			if (!this.#componentRef) this.#createTooltip();
			this.#componentRef?.instance.show();
			return;
		}

		this.#timeout = setTimeout(() => {
			this.#timeout = null;
			if (!this.hasTooltip() || this.#hasInnerTooltip(event)) {
				return;
			}
			if (!this.#componentRef) this.#createTooltip();
			this.#componentRef?.instance.show();
		}, this.tooltipDelay());
	}

	hideTooltip(event?: Event) {
		if (event instanceof PointerEvent && event.pointerType !== 'mouse') {
			return;
		}
		this.#clearTimeout();
		this.#removeTooltip();
	}

	onPointerMove(event: Event) {
		if (event instanceof PointerEvent && event.pointerType !== 'mouse') {
			return;
		}

		if (this.#isTooltipActive()) {
			if (this.#hasInnerTooltip(event)) {
				this.hideTooltip(event);
			}
			return;
		}

		if (!this.hasTooltip() || this.#hasInnerTooltip(event)) {
			return;
		}

		this.showTooltip(event);
	}

	#isTooltipActive() {
		return this.#timeout !== null || this.#componentRef !== null;
	}

	#hasInnerTooltip(event?: Event) {
		if (!event) return false;

		const path = event.composedPath();
		const host = this.#elmRef.nativeElement;
		const targetIndex = path.indexOf(host);
		const innerPath = targetIndex > 0 ? path.slice(0, targetIndex) : [];

		return innerPath.some((node) => {
			if (!(node instanceof HTMLElement)) return false;
			if (node.tagName === 'INS-TOOLTIP') {
				const text = node.getAttribute('text') || (node as { text?: string }).text;
				return Boolean((text && text.trim().length > 0) || node.hasAttribute('data-has-tooltip'));
			}
			return (
				node.matches(INNER_TOOLTIP_SELECTOR) ||
				('tooltip' in node && Boolean((node as { tooltip?: unknown }).tooltip))
			);
		});
	}

	#hideParentTooltips() {
		let currentParent = this.#parentTooltip;
		while (currentParent) {
			currentParent.hideTooltip();
			currentParent = currentParent.#parentTooltip;
		}
	}

	#clearTimeout() {
		if (this.#timeout) {
			clearTimeout(this.#timeout);
			this.#timeout = null;
		}
	}

	#createTooltip() {
		const tooltipContent = this.#getTooltipContent();
		if (!tooltipContent) return;

		const containerRef = this.tooltipContainerRef();
		if (containerRef) {
			this.#renderer.setStyle(containerRef, 'anchor-name', this.anchorName);
		}

		this.#componentRef = this.#viewContainerRef.createComponent(SklTooltipComponent);
		this.#componentRef.setInput('content', tooltipContent);
		this.#componentRef.setInput('id', this.tooltipId);
		this.#componentRef.setInput('anchorName', this.anchorName);
		this.#componentRef.setInput('position', this.tooltipPosition());

		const hostElement = this.#componentRef.location.nativeElement as HTMLElement;
		this.#renderer.appendChild(document.body, hostElement);
		this.#componentRef.changeDetectorRef.detectChanges();
	}

	#removeTooltip() {
		if (this.#componentRef) {
			const ref = this.#componentRef;
			this.#componentRef = null;
			ref.instance.hide();
			const hostElement = ref.location.nativeElement as HTMLElement;
			const animations = hostElement.getAnimations?.() ?? [];
			const cleanup = () => {
				if (hostElement.parentNode) {
					this.#renderer.removeChild(document.body, hostElement);
				}
				ref.destroy();
			};
			if (animations.length) {
				Promise.allSettled(animations.map((a) => a.finished)).then(cleanup);
			} else {
				cleanup();
			}
		}
	}
}
